import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Button, Input, Form, FormGroup, FormLabel, FormError } from './ui'
import './AuthForm.css'

interface AuthFormProps {
  onSuccess?: () => void
}

export const AuthForm: React.FC<AuthFormProps> = ({ onSuccess }) => {
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const { signIn, signUp, resetPassword } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      if (isLogin) {
        const { error } = await signIn(email, password)
        if (error) {
          setError(error.message)
        } else {
          setMessage('Successfully signed in!')
          onSuccess?.()
        }
      } else {
        const { error } = await signUp(email, password)
        if (error) {
          setError(error.message)
        } else {
          setMessage('Check your email for the confirmation link!')
        }
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first')
      return
    }

    setLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await resetPassword(email)
      if (error) {
        setError(error.message)
      } else {
        setMessage('Password reset email sent!')
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-form-container">
      <div className="auth-form">
        <h2>{isLogin ? 'Sign In' : 'Sign Up'}</h2>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup error={error}>
            <FormLabel htmlFor="email" required>Email</FormLabel>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your email"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="password" required>Password</FormLabel>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
              placeholder="Enter your password"
            />
          </FormGroup>

          {error && <FormError>{error}</FormError>}
          {message && <div className="success-message">{message}</div>}

          <Button 
            type="submit" 
            disabled={loading} 
            variant="primary"
            fullWidth
            loading={loading}
          >
            {isLogin ? 'Sign In' : 'Sign Up'}
          </Button>
        </Form>

        <div className="auth-actions">
          <Button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            variant="ghost"
            fullWidth
            disabled={loading}
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </Button>

          {isLogin && (
            <Button
              type="button"
              onClick={handleForgotPassword}
              variant="ghost"
              fullWidth
              disabled={loading}
            >
              Forgot Password?
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
